import config from 'utils/config';
import { getSessionStorageItem, setSessionStorageItem } from 'utils/storage';

/**
 * Session-storage key recording which build already triggered a version-skew
 * auto-reload. Shared by `ChunkSkewErrorBoundary` and the app-router error
 * boundaries (`app/error.tsx`, `app/global-error.tsx`) so the whole tab
 * reloads at most once per loaded build, no matter which boundary caught the
 * skew.
 */
export const CHUNK_SKEW_RELOAD_FLAG_KEY = 'chunk_skew_reloaded';

/**
 * Identifies the bundle currently running in this tab, so the reload guard is
 * scoped to the stale shell that failed rather than to the whole tab session.
 * `NEXT_PUBLIC_COMMIT_REF` is the deploy SHA (what Datadog reports as
 * `version`); when it is unset we fall back to the pages-router build id, then
 * to a constant — in which case the guard degrades to at most one reload per
 * tab session, the previous behaviour.
 */
export function getLoadedBuildId(): string {
    if (config.commitRef) {
        return config.commitRef;
    }
    const nextData = (globalThis as { __NEXT_DATA__?: { buildId?: string } }).__NEXT_DATA__;
    return nextData?.buildId || 'unknown';
}

/**
 * The `(0, mod.export)()` call shape bundlers emit for a *named import*. This is
 * the only "is not a function" TypeError we treat as version skew: the fresh
 * chunk calls an export that the stale shared module already in memory doesn't
 * have, so `mod.export` is undefined at call time.
 *
 *   Chrome:     "(0 , u.useFoo) is not a function"
 *   Safari/JSC: "(0,u.useFoo) is not a function. (In '(0,u.useFoo)()', '(0,u.useFoo)' is undefined)"
 *
 * Ordinary render bugs — a missing optional callback prop ("props.onClose is not
 * a function"), a non-function local — don't produce this shape, so they keep
 * propagating to the nearest parent boundary instead of forcing a reload.
 */
const STALE_EXPORT_CALL_PATTERN = /\(\s*0\s*,\s*[\w$]+(?:\.[\w$]+)+\s*\) is not a function/;

/**
 * Detects errors caused by version skew between the already-loaded bundle and
 * a lazily-fetched chunk from a newer deploy. A mismatched chunk loads
 * successfully — so importWithRetry's ChunkLoadError handling never fires —
 * and instead throws at render time when it calls an export that doesn't
 * exist in the older shared modules already in memory.
 */
export function isVersionSkewError(error: unknown): error is Error {
    if (!(error instanceof Error)) {
        return false;
    }
    if (error.name === 'ChunkLoadError') {
        return true;
    }
    return error.name === 'TypeError' && STALE_EXPORT_CALL_PATTERN.test(error.message);
}

/**
 * Whether a skew auto-reload already ran for the build loaded in this tab.
 * Because the guard records the build that failed, a tab that survives a later
 * deploy can still recover from the new skew.
 */
export function hasReloadedForLoadedBuild(): boolean {
    return getSessionStorageItem(CHUNK_SKEW_RELOAD_FLAG_KEY) === getLoadedBuildId();
}

/**
 * Records that the loaded build is about to trigger a skew auto-reload.
 *
 * Web Storage no-ops in sandboxed webviews and in Safari with cookies blocked.
 * An unpersisted guard would let the reloaded page hit the same error and
 * reload again forever, so this verifies the write by reading it back and
 * returns `false` when it didn't stick — in which case the caller must NOT
 * reload and should fall through to its error UI.
 */
export function persistVersionSkewReloadGuard(): boolean {
    const loadedBuildId = getLoadedBuildId();
    setSessionStorageItem(CHUNK_SKEW_RELOAD_FLAG_KEY, loadedBuildId);
    return getSessionStorageItem(CHUNK_SKEW_RELOAD_FLAG_KEY) === loadedBuildId;
}
